import { isNativeApp } from "./platform";

export interface SaveBlobInput {
  readonly blob: Blob;
  readonly filename: string;
}

export interface DownloadUrlInput {
  readonly url: string;
  readonly filename: string;
}

/** Strip the `data:...;base64,` prefix — Capacitor Filesystem wants raw base64. */
function base64FromBlob(blob: Blob): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = (): void =>
      reject(reader.error ?? new Error("Failed to read blob"));
    reader.onload = (): void => {
      const result = typeof reader.result === "string" ? reader.result : "";
      const comma = result.indexOf(",");
      resolve(comma === -1 ? result : result.slice(comma + 1));
    };
    reader.readAsDataURL(blob);
  });
}

/**
 * Native only: write a blob to the OS cache and open the share sheet so the
 * user can save it to Files, mail it, etc. `<a download>` is a no-op in the iOS
 * WKWebView, so downloads must go through the native share flow. Guarded to a
 * no-op on web — web callers keep their existing anchor/picker path.
 */
export async function saveBlobToDevice(input: SaveBlobInput): Promise<void> {
  if (!isNativeApp()) return;
  // Best-effort and non-throwing: callers fire-and-forget, so a dismissed
  // share sheet or a write failure must not surface as an unhandled rejection.
  try {
    const { Filesystem, Directory } = await import("@capacitor/filesystem");
    const { Share } = await import("@capacitor/share");
    const data = await base64FromBlob(input.blob);
    const written = await Filesystem.writeFile({
      path: input.filename,
      data,
      directory: Directory.Cache,
    });
    try {
      await Share.share({ title: input.filename, url: written.uri });
    } finally {
      // Cache is OS-purged under pressure, but delete eagerly so repeated
      // saves don't accumulate copies.
      await Filesystem.deleteFile({
        path: input.filename,
        directory: Directory.Cache,
      }).catch(() => {});
    }
  } catch {
    // Dismissed sheet or failed write/share — nothing to recover; caller retries.
  }
}

/**
 * Native only: download a (possibly cross-origin) URL to the device. Fetches
 * the bytes and hands them to the share sheet; if the fetch is blocked, opens
 * the URL in the system browser so the OS handles it. No-op on web.
 */
export async function downloadUrlToDevice(
  input: DownloadUrlInput,
): Promise<void> {
  if (!isNativeApp()) return;
  try {
    const response = await fetch(input.url);
    if (!response.ok) throw new Error(`Download failed: ${response.status}`);
    await saveBlobToDevice({
      blob: await response.blob(),
      filename: input.filename,
    });
  } catch {
    const { Browser } = await import("@capacitor/browser");
    await Browser.open({ url: input.url }).catch(() => {});
  }
}

function blobFromBase64(base64: string, type: string): Blob {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type });
}

/**
 * Native file URI → web File. Filesystem, not `convertFileSrc`: iOS cannot
 * serve local files to a remote-hosted page. Null on any failure.
 */
export async function readNativeFile(
  uri: string,
  name: string,
  mimeType: string,
): Promise<File | null> {
  try {
    const { Filesystem } = await import("@capacitor/filesystem");
    const { data } = await Filesystem.readFile({ path: uri });
    const blob =
      typeof data === "string" ? blobFromBase64(data, mimeType) : data;
    return new File([blob], name, { type: mimeType || blob.type });
  } catch {
    return null;
  }
}
